"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSession } from "next-auth/react";
import type { AuthzProject } from "@/types/authz";
import { useActiveOrganization } from "./active-organization";

const STORAGE_KEY = "waas-active-project-id";

export type ActiveProjectContextValue = {
  /** Projects the current user is assigned to (from session). */
  projects: AuthzProject[];
  /** Resolved project for `activeProjectId`, or null. */
  activeProject: AuthzProject | null;
  activeProjectId: string | null;
  setActiveProjectId: (id: string) => void;
};

const ActiveProjectContext = createContext<ActiveProjectContextValue | null>(null);

export function ActiveProjectProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const { organizations, activeOrganizationId } = useActiveOrganization();
  const projects = session?.user?.projects ?? [];

  const scopedProjects = useMemo(() => {
    if (organizations.length <= 1) return projects;
    if (!activeOrganizationId) return projects;
    return projects.filter((p) => p.organizationId === activeOrganizationId);
  }, [organizations.length, activeOrganizationId, projects]);

  const projectIdsKey = useMemo(
    () =>
      scopedProjects
        .map((p) => p.id)
        .slice()
        .sort()
        .join("|"),
    [scopedProjects],
  );

  const [activeProjectId, setActiveProjectIdState] = useState<string | null>(null);

  useEffect(() => {
    if (status === "loading") return;

    const frame = requestAnimationFrame(() => {
      if (scopedProjects.length === 0) {
        setActiveProjectIdState(null);
        return;
      }

      if (scopedProjects.length === 1) {
        const id = scopedProjects[0].id;
        setActiveProjectIdState(id);
        try {
          localStorage.setItem(STORAGE_KEY, id);
        } catch {
          /* ignore */
        }
        return;
      }

      const ids = new Set(scopedProjects.map((p) => p.id));

      setActiveProjectIdState((current) => {
        if (current && ids.has(current)) return current;

        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored && ids.has(stored)) return stored;
        } catch {
          /* ignore */
        }

        return scopedProjects[0]?.id ?? null;
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [status, projectIdsKey, scopedProjects]);

  const setActiveProjectId = useCallback(
    (id: string) => {
      const ids = new Set(scopedProjects.map((p) => p.id));
      if (!ids.has(id)) return;
      setActiveProjectIdState(id);
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch {
        /* ignore */
      }
    },
    [scopedProjects],
  );

  const activeProject = scopedProjects.find((p) => p.id === activeProjectId) ?? null;

  const value = useMemo(
    (): ActiveProjectContextValue => ({
      projects: scopedProjects,
      activeProject,
      activeProjectId,
      setActiveProjectId,
    }),
    [scopedProjects, activeProject, activeProjectId, setActiveProjectId],
  );

  return <ActiveProjectContext.Provider value={value}>{children}</ActiveProjectContext.Provider>;
}

export function useActiveProject(): ActiveProjectContextValue {
  const ctx = useContext(ActiveProjectContext);
  if (!ctx) {
    throw new Error("useActiveProject must be used within ActiveProjectProvider");
  }
  return ctx;
}
